/**
 * LaunchBar — sticky top bar with session status, edit count, warning
 * count, usage/cost, and the [Launch Campaign] button.
 *
 * Launch is disabled while any blocker is present.
 */

import React from 'react';
import { STATUS } from '../../hooks/useAgentSession';
import './LaunchBar.css';

// Opus 4.7 pricing — keep in sync with shared/models.md.
// Input $5/M, output $25/M, cache read ~10% of input ($0.50/M).
// Cache writes aren't tracked separately in our usage object yet — when
// they are, add ((usage.cache_creation_input_tokens / 1e6) * 6.25) for
// 5-minute TTL writes (1.25× input).
function estimateUsd(usage) {
  if (!usage) return 0;
  const input = (usage.input_tokens || 0) / 1e6 * 5.00;
  const output = (usage.output_tokens || 0) / 1e6 * 25.00;
  const cacheRead = (usage.cache_read_input_tokens || 0) / 1e6 * 0.50;
  return input + output + cacheRead;
}

function fmtUsd(n) {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

const STATUS_LABEL = {
  [STATUS.INITIALIZING]: { text: 'Starting…', color: '#888' },
  [STATUS.STREAMING]: { text: 'Agent working', color: '#0969da' },
  [STATUS.IDLE]: { text: 'Agent ready', color: '#1f883d' },
  [STATUS.WAITING_FOR_USER]: { text: 'Waiting for you', color: '#bf8700' },
  [STATUS.FINALIZED]: { text: 'Approved — launching', color: '#1f883d' },
  [STATUS.TERMINATED]: { text: 'Session ended', color: '#888' },
  [STATUS.ERROR]: { text: 'Error', color: '#cf222e' },
};

const LaunchBar = ({
  sessionStatus, editCount, hasEdits, saving,
  onSave, onLaunch, onInterrupt, onNewSession, restarting,
  warnings, usage, showUsage = false,
}) => {
  const statusMeta = STATUS_LABEL[sessionStatus] || STATUS_LABEL[STATUS.IDLE];
  const blockerCount = (warnings || []).filter(w => w.severity === 'blocker').length;
  const warningCount = (warnings || []).filter(w => w.severity === 'warning').length;

  const launchDisabled =
    blockerCount > 0
    || sessionStatus === STATUS.FINALIZED
    || sessionStatus === STATUS.TERMINATED
    || sessionStatus === STATUS.ERROR
    || sessionStatus === STATUS.INITIALIZING
    || saving;

  const saveDisabled = !hasEdits || saving;

  // "New Session" is available any time we have a live session to archive.
  // Hidden during the initial mount (no session yet to replace) and once the
  // session has been approved (the launch hand-off makes a restart moot).
  const newSessionHidden =
    sessionStatus === STATUS.INITIALIZING
    || sessionStatus === STATUS.FINALIZED;
  const newSessionDisabled = saving || restarting;

  return (
    <div className="launch-bar">
      <div className="launch-bar-left">
        <span className="launch-bar-status" style={{ color: statusMeta.color }}>
          ● {statusMeta.text}
        </span>
        {hasEdits && (
          <span className="launch-bar-chip launch-bar-chip-unsaved">
            Unsaved edits
          </span>
        )}
        {typeof editCount === 'number' && editCount > 0 && (
          <span className="launch-bar-chip">
            {editCount} edit{editCount === 1 ? '' : 's'}
          </span>
        )}
        {blockerCount > 0 && (
          <span className="launch-bar-chip launch-bar-chip-blocker">
            {blockerCount} blocker{blockerCount === 1 ? '' : 's'}
          </span>
        )}
        {warningCount > 0 && (
          <span className="launch-bar-chip launch-bar-chip-warning">
            {warningCount} warning{warningCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="launch-bar-right">
        {showUsage && usage && (usage.input_tokens > 0 || usage.output_tokens > 0) && (
          <span
            className="launch-bar-usage"
            title={
              `Cumulative session usage:\n`
              + `  Input:       ${usage.input_tokens.toLocaleString()} tokens (uncached)\n`
              + `  Cache reads: ${usage.cache_read_input_tokens.toLocaleString()} tokens\n`
              + `  Output:      ${usage.output_tokens.toLocaleString()} tokens\n\n`
              + `Pricing (Opus 4.7): $5/M input · $0.50/M cache read · $25/M output`
            }
          >
            {fmtUsd(estimateUsd(usage))}
            {' · '}
            {(usage.input_tokens / 1000).toFixed(1)}k in / {(usage.output_tokens / 1000).toFixed(1)}k out
            {usage.cache_read_input_tokens > 0 && (
              <> · {(usage.cache_read_input_tokens / 1000).toFixed(1)}k cached</>
            )}
          </span>
        )}
        {sessionStatus === STATUS.STREAMING && onInterrupt && (
          <button className="launch-bar-btn-secondary" onClick={onInterrupt}>
            Stop
          </button>
        )}
        {onSave && (
          <button
            className="launch-bar-btn-secondary"
            onClick={onSave}
            disabled={saveDisabled}
            title={hasEdits ? 'Save edits' : 'No pending edits'}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {onNewSession && !newSessionHidden && (
          <button
            className="launch-bar-btn-secondary"
            onClick={onNewSession}
            disabled={newSessionDisabled}
            title="Archive this conversation and start a fresh agent session for the same campaign"
          >
            {restarting ? 'Starting…' : 'New Session'}
          </button>
        )}
        <button
          className="launch-bar-btn-primary"
          onClick={onLaunch}
          disabled={launchDisabled}
          title={blockerCount > 0 ? 'Resolve blockers before launching' : 'Approve and launch'}
        >
          Launch Campaign
        </button>
      </div>
    </div>
  );
};

export default LaunchBar;
